import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { word } from "@modules/core/utils/i18n"
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from "lucide-react"
import type { ReactNode } from "react"

type BoxDashboardPaginatedBlockProps = {
  header: ReactNode
  children: ReactNode
  emptyState: ReactNode
  isEmpty: boolean
  isLoading: boolean
  loadingComponent: ReactNode
  isExpanded: boolean
  showShowMore: boolean
  showShowLess: boolean
  showPagination: boolean
  onShowMore: () => void
  onShowLess: () => void
  totalPages: number
  currentPage: number
  onPageChange: (page: number) => void
  collapsedHeight?: string
  expandedHeight?: string
}

export const BoxDashboardPaginatedBlock = ({
  header,
  children,
  emptyState,
  isEmpty,
  isLoading,
  loadingComponent,
  isExpanded,
  showShowMore,
  showShowLess,
  showPagination,
  onShowMore,
  onShowLess,
  totalPages,
  currentPage,
  onPageChange,
  collapsedHeight = "h-129",
  expandedHeight = "h-197.5",
}: BoxDashboardPaginatedBlockProps) => {
  const hasContent = !isEmpty

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      onPageChange(currentPage + 1)
    }
  }

  const handlePrevPage = () => {
    if (currentPage > 0) {
      onPageChange(currentPage - 1)
    }
  }

  return (
    <div
      className={cn(
        "flex flex-col overflow-clip rounded-4xl bg-surface-foreground transition-all duration-300",
        isExpanded ? expandedHeight : collapsedHeight,
      )}
    >
      {/* Header */}
      {header}

      {/* Content */}
      <div className="mt-3 flex flex-1 flex-col overflow-hidden pt-2">
        {isLoading ? loadingComponent : isEmpty ? emptyState : children}
      </div>

      {/* Footer */}
      {hasContent && (showShowMore || showShowLess || showPagination) && (
        <div className="mt-auto bg-linear-to-b from-surface-background from-[-100%] to-surface-foreground px-6 py-4">
          <div className="flex items-center justify-center gap-4">
            {/* Show More / Show Less */}
            {showShowMore && (
              <Button variant="underline" onClick={onShowMore}>
                <ChevronDown className="size-4" />
                <Text fontStyle="LL">{word("global.show_more")}</Text>
              </Button>
            )}
            {showShowLess && (
              <Button variant="underline" onClick={onShowLess}>
                <ChevronUp className="size-4" />
                <Text fontStyle="LL">{word("global.show_less")}</Text>
              </Button>
            )}

            {/* Pagination - only show when expanded */}
            {showPagination && isExpanded && (
              <div className="flex items-center gap-0.5">
                <button
                  className={cn(
                    "flex size-10.5 items-center justify-center rounded-full",
                    currentPage === 0 ? "text-textOnSurface-disabled" : "cursor-pointer text-textOnSurface-subtle",
                  )}
                  onClick={handlePrevPage}
                  disabled={currentPage === 0}
                >
                  <ChevronLeft className="size-3.5" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    className={cn(
                      "flex size-10.5 cursor-pointer items-center justify-center rounded-full text-sm text-textOnSurface-subtle",
                      currentPage === i ? "bg-surface-background-bottomSheet font-semibold" : "font-normal",
                    )}
                    onClick={() => onPageChange(i)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  className={cn(
                    "flex size-10.5 items-center justify-center rounded-full",
                    currentPage >= totalPages - 1
                      ? "text-textOnSurface-disabled"
                      : "cursor-pointer text-textOnSurface-subtle",
                  )}
                  onClick={handleNextPage}
                  disabled={currentPage >= totalPages - 1}
                >
                  <ChevronRight className="size-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
